<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import ApiServiceV1 from '@/api/adminApi/AdminApiV1'
import BaseOdometer from '@/components/general/BaseOdometer.vue'
import { GtmEventEmitter } from '@/utils/gtmEventEmitter'
import { CartService } from '@/services/cartService'
import { useAppStore } from '@/stores/AppStore'
import { useCartStore } from '@/stores/CartStore'
import { useModalStore } from '@/stores/ModalStore'
import { isAxiosError } from 'axios'
import { useNoticeStore } from '@/stores/NoticeStore'

const { addNotice } = useNoticeStore();

const props = defineProps({
  product: {
    type: Object,
    default: null,
  },
  cartMix: {
    type: Object,
    default: null,  
  },
  type: {
    type: String,
    default: 'product'
  },
  minified: {
    type: Boolean,
    default: true
  },
  large: {
    type: Boolean,
    default: false
  }
})

const { openModal } = useModalStore()
const appStore = useAppStore()
const cartStore = useCartStore()
const { getCurrentCustomer } = storeToRefs(appStore)
const { t } = useI18n()

defineEmits(['cartOperation'])

const quantityInCart = computed(() => props.type === 'product' ? cartStore.getProductQuantity(props.product.id) : cartStore.getMixQuantity(props.cartMix.mixId))

const showDiscountModal = () => {
  return (
    props.product.quantityDiscounts &&
    (!props.product.plusClubPrice || !getCurrentCustomer.value?.inPlusClub) &&
    props.product.state === 'available'
  )
}

const increaseQuantityProduct = async () => {
  if (showDiscountModal()) {
    openModal({
      id: 'modal-quantity-discount-table',
      params: {
        product: props.product,
      },
    })
  }
  try {
    const cartService = new CartService()
    await cartService.addProduct(props.product.id, 1)
    const {
      data: { product },
    } = await ApiServiceV1.get(`products/${props.product.permalink}`)
    GtmEventEmitter.addToCart({
      product: product,
      quantity: 1,
      isFavorite: appStore.isFavorite(product.id),
    })
  } catch (error) {
    openModal({
      id: 'modal-alert',
      title: isAxiosError(error) ? error.response?.data.msg : error,
    })
  }
}

const decreaseQuantityProduct = async () => {
  try {
    const cartService = new CartService()
    const cartItem = cartStore.getProductInCart(props.product.id)
    if (quantityInCart.value > 1) {
      await cartService.changeProductQuantity(props.product.id, -1)
      const {
        data: { product },
      } = await ApiServiceV1.get(`products/${props.product.permalink}`)
      GtmEventEmitter.removeFromCart({
        product: product,
        quantity: 1,
        isFavorite: appStore.isFavorite(product.id),
      })
    } else {
      openModal({
        id: 'modal-remove-item',
        params: {
          itemId: cartItem?.id,
          name: cartItem?.name,
          noticeText: t('product_quantity_removed', { name: cartItem?.name }),
          removeCallback: async (itemId: number) => {
            await cartService.deleteProduct(itemId)
            const {
              data: { product },
            } = await ApiServiceV1.get(`products/${cartItem?.permalink}`)
            GtmEventEmitter.removeFromCart({
              product: product,
              quantity: 1,
              isFavorite: cartItem?.productID ? appStore.isFavorite(cartItem.productID) : false,
            })
          },
        },
      })
    }
  } catch (error) {
    openModal({
      id: 'modal-alert',
      title: isAxiosError(error) ? error.response?.data.msg : error,
    })
  }
}

const increaseQuantityMix = async () => {
  const cartService = new CartService()
  await cartService.changeMixQuantity(props.cartMix.id, 1)
}

const decreaseQuantityMix = async () => {
  const cartService = new CartService()
  if (quantityInCart.value > 1) {
    await cartService.changeMixQuantity(props.cartMix.id, -1)
  } else {
    openModal({
      id: 'modal-remove-item',
      params: {
        itemId: props.cartMix.id,
        name: props.cartMix.name,
        async removeCallback(mixId: string) {
          const { data } = await cartService.deleteMix(mixId)

          addNotice({
            text: t('mix_quantity_removed', { name: data.name }),
            background: 'bg-warning',
          })
        },
      },
    })
  }
}

const increase = async () => {
  if (props.type === 'product') {
    increaseQuantityProduct()
  } else { increaseQuantityMix() }
}
const decrease = async () => {
  if (props.type === 'product') {
    decreaseQuantityProduct()
  } else { decreaseQuantityMix() }
}
</script>

<template>
  <div
    class="relative btn-to-cart"
    :class="{ 'large': large === true }"
  >
    <div
      class="text-center flex right-0 top-0 rounded-pill"
    >
      <div
        class="quantity-bar right-0 top-0 bg-primary-200 rounded-pill flex flex-row items-center zindex-2"
        :class="[
          {'active': quantityInCart > 0 && minified === true},
          minified === true ? 'absolute' : 'active'
        ]"
      >
        <button
          class="bg-primary-200 text-primary p-0 border-0 rounded-pill"
          @click="decrease"
        >
          <i
            :class="[
              quantityInCart <= 1 ? 'mi-bin' : 'mi-minus',
              large ? 'icon-24' : 'icon-16'
            ]"
          />
        </button>
        <span
          class="odometer-wrap flex-fill rounded-pill bg-white border overflow-hidden flex justify-center items-center py-1"
        >
          <BaseOdometer :number="quantityInCart" />
        </span>
      </div>
      <button
        class="bg-success text-white p-0 border-0 rounded-pill relative zindex-11"
        :class="{ 'ml-n5': minified === false }"
        @click="increase"
      >
        <i
          :class="[
            quantityInCart > 0 ? 'mi-plus' : 'mi-cart',
            large ? 'icon-24' : 'icon-16'
          ]"
        />
      </button>
    </div>
  </div>
</template>

<style lang="scss">
.btn-to-cart {
    button {
        width: 34px;
        height: 34px;
    }
    &.large {
      button {
          width: 52px;
          height: 52px;
      }
      .quantity-bar {
          width: 160px;
          padding-right: 56px;
      }
    }

    .quantity-bar {
        width: 115px;
        padding-right: 36px;
        pointer-events: none;
        opacity: 0;
        transform: translateX(10%);
        transition: all 0.2s ease-in-out;

        &.active {
            pointer-events: all;
            opacity: 1;
            transform: translateX(0%);
        }

        .odometer-wrap {
            padding-left: 2px;
            padding-right: 2px;
        }
    }

    @media screen and (max-width: 420px) {
      button {
        width: 44px;
        height: 44px;
      }
      .quantity-bar {
        width: 130px;

        &.active {
            margin-right: 10px;
        }
      }
    }
}
</style>

<i18n>
  {
    "cs": {
    "to_cart": "Do košíku"
},
    "sk": {
    "to_cart": "Do košíka"
},
    "pl": {
    "to_cart": "Do koszyka"
},
    "hu": {
    "to_cart": "Kosárba"
},
    "de": {
    "to_cart": "In den Warenkorb"
},
    "at": {
    "to_cart": "In den Warenkorb"
},
    "ro": {
    "to_cart": "În coș"
},
    "fr": {
    "to_cart": "Ajouter au panier"
}
  }
</i18n>
